import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { loadAccessControls, selectAccessControl } from '../redux/accessControls/slice'
import { PermissionLevels, Permissions } from '../types/AccessControls'
import { checkUserHasPermission, checkUserHasViewPermission } from '../helper/checkUserPermission'

/**
 * Checks whether the current user has been granted **any** of the specified permissions (OR logic).
 *
 * For each `[PermissionLevel, Permission]` pair in `checks`, the hook first verifies view-level
 * access via viewPermissions, then loads the page-level permission data from the Redux store
 * (fetching from the API if not yet cached) and returns `true` as soon as any single check passes.
 *
 * @param checks - Array of `[PermissionLevel, Permission]` tuples to evaluate.
 * @returns `[hasPermission, isLoading]`
 *   - `hasPermission` — `true` if the user satisfies at least one of the checks.
 *   - `isLoading`     — `true` while any permission level's data is still being fetched.
 *
 * @example
 * const [granted, loading] = useAccessGrant([
 *   [PermissionLevels.NEW_ELEVATE_BATCH_REPORT, Permissions.LIST],
 *   [PermissionLevels.DAILYSTUDYPLAN,           Permissions.LIST],
 * ])
 */
const useAccessGrant = (checks: [PermissionLevels, Permissions][]): [boolean, boolean] => {
  const accessControls = useSelector(selectAccessControl)
  const dispatch = useDispatch()
  const viewPermissions = accessControls.data?.viewPermissions

  // If viewPermissions are loaded, check if user has view access to any of the required levels.
  // If none pass the view check, deny immediately without fetching page-level permissions.
  const hasViewAccess = viewPermissions
    ? checks.some(([level]) => checkUserHasViewPermission(level, viewPermissions))
    : true // If viewPermissions not yet loaded, don't block — let page-level check proceed

  useEffect(() => {
    if (accessControls.hasError) return
    if (!hasViewAccess) return // No need to fetch page permissions if view access is denied
    checks.forEach(([level]) => {
      if (!accessControls.data?.permissionLevels?.[level]) {
        dispatch(loadAccessControls(level))
      }
    })
  }, [accessControls.data?.permissionLevels, accessControls.hasError, hasViewAccess])

  // If view access is denied, immediately return no permission
  if (viewPermissions && !hasViewAccess) {
    return [false, false]
  }

  // A failed fetch is a terminal state, not a pending one. The effect above stops
  // dispatching once `hasError` is set, so treating it as loading leaves the route
  // stuck on <Loader /> forever instead of resolving to a redirect.
  const isLoading =
    !accessControls.hasError &&
    checks.some(([level]) => !accessControls.data?.permissionLevels?.[level])
  const hasPermission = checks.some(([level, permission]) =>
    checkUserHasPermission(permission, accessControls.data?.permissionLevels?.[level]),
  )

  return [hasPermission, isLoading]
}

export default useAccessGrant
